import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useAuth } from "@project/hooks/useAuth";
import { Check, Heart, X, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@project/components/ui/button";
import { Checkbox } from "@project/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import { MobileNumberField } from "@project/components/mobile-number-field";
import { openRazorpayCheckout } from "@project/donate/components/razorpay-checkout";
import {
  PaymentVerificationOverlay,
  type PaymentVerificationStatus,
} from "@project/donate/components/payment-verification-overlay";
import { getApiErrorMessage } from "@project/lib/api-error";
import { publicTiffinSevaDonationService, type TiffinSevaCreateOrderPayload } from "@project/services/donate/tiffin-seva-donation.service";

type FormState = {
  scheme: string;
  times: string;
  donor_name: string;
  mobile_number: string;
  email: string;
  city: string;
  full_address: string;
  state: string;
  pincode: string;
  country: string;
  occasion: string;
  display_name: string;
  special_instructions: string;
  is_anonymous: boolean;
  info_confirmed: boolean;
  contact_consent: boolean;
};

const emptyForm = (): FormState => ({
  scheme: "",
  times: "1",
  donor_name: "",
  mobile_number: "",
  email: "",
  city: "",
  full_address: "",
  state: "Gujarat",
  pincode: "",
  country: "India",
  occasion: "",
  display_name: "",
  special_instructions: "",
  is_anonymous: false,
  info_confirmed: false,
  contact_consent: false,
});

export type TiffinSevaDonationModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function TiffinSevaDonationModal({
  open,
  onOpenChange,
}: TiffinSevaDonationModalProps) {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [schemes, setSchemes] = useState<any[]>([]);
  const [loadingSchemes, setLoadingSchemes] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [verificationOpen, setVerificationOpen] = useState(false);
  const [verificationStatus, setVerificationStatus] =
    useState<PaymentVerificationStatus>("idle");

  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Reset form and fetch schemes when modal opens
  useEffect(() => {
    if (open) {
      setForm(emptyForm());
      setSubmitting(false);
      setVerificationOpen(false);
      setVerificationStatus("idle");
      setLoadingSchemes(true);

      publicTiffinSevaDonationService.listActiveSchemes()
        .then(res => {
          setSchemes(res);
          if (res.length > 0) {
            setForm(prev => ({ ...prev, scheme: String(res[0].id) }));
          }
        })
        .catch(err => {
          console.error("Failed to load tiffin schemes:", err);
          toast.error("Could not load donation schemes");
        })
        .finally(() => setLoadingSchemes(false));
    }
  }, [open]);

  const patch = (updates: Partial<FormState>) =>
    setForm(prev => ({ ...prev, ...updates }));

  const selectedSchemeObj = useMemo(() => {
    return schemes.find(s => String(s.id) === form.scheme);
  }, [schemes, form.scheme]);

  const estimatedTotal = useMemo(() => {
    if (!selectedSchemeObj) return 0;
    const priceNum = parseFloat(selectedSchemeObj.price);
    const timesNum = parseInt(form.times, 10) || 0;
    return priceNum * timesNum;
  }, [selectedSchemeObj, form.times]);

  const buildPayload = (): TiffinSevaCreateOrderPayload => {
    if (!form.info_confirmed) {
      throw new Error("Please confirm the information to proceed.");
    }
    if (!form.scheme) {
      throw new Error("Please select a donation scheme.");
    }
    const timesParsed = parseInt(form.times, 10);
    if (Number.isNaN(timesParsed) || timesParsed <= 0) {
      throw new Error("Please enter a valid number of times.");
    }
    if (!form.donor_name.trim()) {
      throw new Error("Donor Name is required.");
    }
    if (!/^\d{10}$/.test(form.mobile_number.trim())) {
      throw new Error("Enter a valid 10-digit mobile number.");
    }
    if (!form.email.trim()) {
      throw new Error("Email address is required.");
    }
    if (!form.city.trim()) {
      throw new Error("City is required.");
    }
    if (!form.full_address.trim()) {
      throw new Error("Full Address is required.");
    }
    if (!form.state.trim()) {
      throw new Error("State is required.");
    }
    if (!/^\d{6}$/.test(form.pincode.trim())) {
      throw new Error("Enter a valid 6-digit pincode.");
    }

    return {
      scheme: Number(form.scheme),
      times: timesParsed,
      donor_name: form.donor_name.trim(),
      mobile_number: form.mobile_number.trim(),
      email: form.email.trim(),
      city: form.city.trim(),
      full_address: form.full_address.trim(),
      state: form.state.trim(),
      pincode: form.pincode.trim(),
      country: form.country.trim() || "India",
      occasion: form.occasion.trim() || undefined,
      display_name: form.display_name.trim() || undefined,
      special_instructions: form.special_instructions.trim() || undefined,
      is_anonymous: form.is_anonymous,
      info_confirmed: form.info_confirmed,
      contact_consent: form.contact_consent,
    };
  };

  const handleProceed = async () => {
    if (!isAuthenticated) {
      toast.info("Please log in to continue with your donation");
      onOpenChange(false);
      navigate({
        to: "/login",
        search: {
          redirectTo: "/tiffin-seva",
        },
      });
      return;
    }

    try {
      const payload = buildPayload();
      setSubmitting(true);
      const order = await publicTiffinSevaDonationService.createOrder(payload);

      // Close the Radix Dialog BEFORE opening Razorpay
      onOpenChange(false);

      await new Promise(resolve => window.setTimeout(resolve, 80));

      await openRazorpayCheckout({
        order_id: order.order_id,
        amount: order.amount,
        currency: order.currency ?? "INR",
        key: order.key_id,
        name: "Jivandhara Old Age Home",
        description: `Tiffin Seva Sponsorship: ${order.scheme_name || "Scheme"}`,
        prefill: {
          name: payload.donor_name,
          email: payload.email,
          contact: payload.mobile_number,
        },
        onDismiss: () => {
          setSubmitting(false);
          onOpenChange(true);
          toast.message("Payment cancelled. You can try again anytime.");
        },
        onSuccess: async response => {
          setVerificationOpen(true);
          setVerificationStatus("verifying");
          try {
            const verify = await publicTiffinSevaDonationService.verifyPayment({
              razorpay_order_id: order.order_id,
              razorpay_payment_id: String(response.razorpay_payment_id),
              razorpay_signature: String(response.razorpay_signature),
            });
            setVerificationStatus("success");
            toast.success(
              verify.receipt_no
                ? `Donation successful. Receipt No: ${verify.receipt_no}`
                : "Donation successful. Thank you for your warm support!"
            );
            window.setTimeout(() => {
              setVerificationOpen(false);
              setVerificationStatus("idle");
            }, 1600);
          } catch (verifyError) {
            setVerificationOpen(false);
            setVerificationStatus("idle");
            toast.error(
              getApiErrorMessage(
                verifyError,
                "Payment received but verification failed. Please contact support."
              )
            );
          } finally {
            setSubmitting(false);
          }
        },
      });
    } catch (error: any) {
      setSubmitting(false);
      onOpenChange(true);
      toast.error(error.message || getApiErrorMessage(error, "Could not start payment"));
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-3xl flex-col gap-0 overflow-hidden rounded-2xl p-0">
          <DialogHeader className="shrink-0 border-b border-border px-5 py-4 pr-12 text-left sm:px-6">
            <div className="flex items-start gap-3">
              <div className="bg-rose-50 dark:bg-rose-950/20 grid h-10 w-10 shrink-0 place-items-center rounded-xl text-rose-500">
                <Heart className="h-5 w-5 fill-current" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold text-foreground sm:text-xl">
                  Tiffin Seva Sponsorship Form
                </DialogTitle>
                <DialogDescription className="mt-0.5 text-sm text-muted-foreground">
                  Support Jivandhara Satvik meals delivery to elderly & needy
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="min-h-0 flex-1 space-y-7 overflow-y-auto px-5 py-5 sm:px-6">
            {/* Sponsorship Details */}
            <section className="space-y-4">
              <h3 className="font-serif text-xl font-semibold text-foreground">
                Sponsorship Details
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label data-required="true">Choose Scheme</Label>
                  <Select
                    value={form.scheme}
                    onValueChange={scheme => patch({ scheme })}
                    disabled={loadingSchemes || schemes.length === 0}
                  >
                    <SelectTrigger className="bg-muted/30 rounded-xl">
                      <SelectValue placeholder={loadingSchemes ? "Loading..." : "Select Scheme"} />
                    </SelectTrigger>
                    <SelectContent>
                      {schemes.map(s => (
                        <SelectItem key={s.id} value={String(s.id)}>
                          {s.name} (₹{parseFloat(s.price).toLocaleString("en-IN")})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label data-required="true">Times (Multiplier)</Label>
                  <Input
                    required
                    type="number"
                    min="1"
                    value={form.times}
                    onChange={e => patch({ times: e.target.value })}
                    className="bg-muted/30 rounded-xl"
                  />
                </div>

                {selectedSchemeObj && (
                  <div className="sm:col-span-2 bg-green-50/50 dark:bg-green-950/10 border border-rose-100 dark:border-rose-950/20 rounded-xl p-3.5 flex items-center justify-between">
                    <span className="text-xs font-semibold text-green-800 dark:text-green-400 flex items-center gap-1.5">
                      <Sparkles className="h-4 w-4" /> Sponsorship Amount Summary
                    </span>
                    <span className="text-sm font-bold text-rose-900 dark:text-rose-300">
                      <span className="text-gray-500 dark:text-gray-400"> ₹{parseFloat(selectedSchemeObj.price).toLocaleString("en-IN")} × {form.times || 1} </span> = ₹{estimatedTotal.toLocaleString("en-IN")}
                    </span>
                  </div>
                )}
              </div>
            </section>

            {/* Your Details */}
            <section className="space-y-4">
              <h3 className="font-serif text-xl font-semibold text-foreground">
                Your Details
              </h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label data-required="true">Donor Name</Label>
                  <Input
                    required
                    value={form.donor_name}
                    onChange={e => patch({ donor_name: e.target.value })}
                    placeholder="Enter your full name"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div>
                  <Label data-required="true">Mobile Number</Label>
                  <MobileNumberField
                    required
                    value={form.mobile_number}
                    onChange={mobile_number => patch({ mobile_number })}
                    placeholder="10-digit phone number"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div>
                  <Label data-required="true">Email</Label>
                  <Input
                    type="email"
                    required
                    value={form.email}
                    onChange={e => patch({ email: e.target.value })}
                    placeholder="name@example.com"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div>
                  <Label data-required="true">City</Label>
                  <Input
                    required
                    value={form.city}
                    onChange={e => patch({ city: e.target.value })}
                    placeholder="Ahmedabad"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label data-required="true">Full Address</Label>
                  <Textarea
                    required
                    rows={2}
                    value={form.full_address}
                    onChange={e => patch({ full_address: e.target.value })}
                    placeholder="Street name, society, flat number"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div>
                  <Label data-required="true">State</Label>
                  <Input
                    required
                    value={form.state}
                    onChange={e => patch({ state: e.target.value })}
                    placeholder="Gujarat"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div>
                  <Label data-required="true">Pincode</Label>
                  <Input
                    required
                    inputMode="numeric"
                    maxLength={6}
                    value={form.pincode}
                    onChange={e =>
                      patch({
                        pincode: e.target.value.replace(/\D/g, "").slice(0, 6),
                      })
                    }
                    placeholder="380001"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>Country</Label>
                  <Input
                    value={form.country}
                    onChange={e => patch({ country: e.target.value })}
                    placeholder="India"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
              </div>
            </section>

            {/* Additional Info */}
            <section className="space-y-4">
              <h3 className="font-serif text-xl font-semibold text-foreground">
                Additional Details
              </h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label>Occasion</Label>
                  <Input
                    value={form.occasion}
                    onChange={e => patch({ occasion: e.target.value })}
                    placeholder="e.g. Birthday, Anniversary, In memory of..."
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>Name to Display on Board</Label>
                  <Input
                    value={form.display_name}
                    onChange={e => patch({ display_name: e.target.value })}
                    placeholder="In loving memory of... / On behalf of..."
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>Special Instructions</Label>
                  <Textarea
                    rows={3}
                    maxLength={500}
                    value={form.special_instructions}
                    onChange={e =>
                      patch({ special_instructions: e.target.value })
                    }
                    placeholder="Any specific requests regarding tiffin delivery dates or instructions..."
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="bg-muted/20 flex cursor-pointer items-start gap-3 rounded-xl border border-border px-4 py-3">
                    <Checkbox
                      checked={form.is_anonymous}
                      onCheckedChange={checked =>
                        patch({ is_anonymous: checked === true })
                      }
                      className="mt-0.5"
                    />
                    <span>
                      <span className="block text-sm font-semibold text-foreground">
                        Keep my donation anonymous
                      </span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        Admin can still securely retain details. Public updates will show 'Anonymous Donor'.
                      </span>
                    </span>
                  </label>
                </div>
              </div>
            </section>

            {/* Consent */}
            <section className="space-y-4">
              <h3 className="font-serif text-xl font-semibold text-foreground">
                Consent
              </h3>
              <div className="bg-muted/20 space-y-3 rounded-xl border border-border px-4 py-3">
                <label className="flex cursor-pointer items-start gap-3">
                  <Checkbox
                    checked={form.info_confirmed}
                    onCheckedChange={checked =>
                      patch({ info_confirmed: checked === true })
                    }
                    className="mt-0.5"
                  />
                  <span className="text-sm text-foreground">
                    I confirm that the information provided is correct and agree to proceed with this donation. <span className="text-destructive">*</span>
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-3">
                  <Checkbox
                    checked={form.contact_consent}
                    onCheckedChange={checked =>
                      patch({ contact_consent: checked === true })
                    }
                    className="mt-0.5"
                  />
                  <span className="text-sm text-foreground">
                    I agree to be contacted regarding this Tiffin Seva sponsorship. (Optional)
                  </span>
                </label>
              </div>
            </section>
          </div>

          <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-border px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
            <Button
              type="button"
              variant="outline"
              className="rounded-xl"
              disabled={submitting}
              onClick={() => onOpenChange(false)}
            >
              <X className="mr-1 h-4 w-4" />
              Cancel
            </Button>
            <Button
              type="button"
              className="hover:bg-primary/90 rounded-xl bg-primary font-semibold text-primary-foreground"
              disabled={submitting || schemes.length === 0}
              onClick={() => void handleProceed()}
            >
              <Check className="mr-1 h-4 w-4" />
              {submitting ? "Processing…" : `Proceed to Pay ₹${estimatedTotal.toLocaleString("en-IN")}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <PaymentVerificationOverlay
        open={verificationOpen}
        status={verificationStatus}
      />
    </>
  );
}
